import React, { useMemo, type ReactElement } from 'react';
// @ts-ignore implicit 'any' type
import BlockContent from '@sanity/block-content-to-react';
import {
  Heading,
  type HeadingLevel,
  HeadingLevels,
  type HeadingProps,
  type HeadingTag,
  Superscript,
  type SuperscriptProps,
  Text,
  type TextProps
} from '@superside/ui';
import { QuoteComponent } from './QuoteComponent';

type NormalRendererProps = {
  renderNormalAsHeading?: HeadingOptions;
  renderNormal?: ReactElement;
} & (HeadingProps | TextProps);

const BlockRenderers = {
  body: (props: TextProps) => <Text {...props} />,
  normal: (props: NormalRendererProps) => {
    const { renderNormalAsHeading, renderNormal, ...rest } = props;

    if (renderNormal) {
      return React.cloneElement(renderNormal, rest);
    }

    if (!renderNormalAsHeading) {
      return <Text as='p' {...(rest as TextProps)} />;
    }

    const children = rest.children;
    const hasHeadingMark = useMemo(() => {
      if (!Array.isArray(children)) {
        return false;
      }

      return children?.some((child) => {
        return typeof child === 'object' && child.props?.node?.mark?._type === 'customHeading';
      });
    }, [children]);

    if (hasHeadingMark) {
      return <span {...(rest as HeadingProps)} />;
    }

    const { as = undefined, level } =
      typeof renderNormalAsHeading === 'object'
        ? renderNormalAsHeading
        : { level: renderNormalAsHeading };

    return <Heading as={as} level={level} {...(rest as HeadingProps)} />;
  },
  disclaimer: (props: TextProps) => <Text as='p' size='sm' {...props} />,
  blockquote: (props: TextProps) => <QuoteComponent {...props} />,
  superscript: (props: SuperscriptProps) => <Superscript as='h3' {...props} />,
  mega: (props: HeadingProps) => <Heading level='mega' {...props} />,
  epic: (props: HeadingProps) => <Heading level='epic' {...props} />,
  h1: (props: HeadingProps) => <Heading level='h1' {...props} />,
  h2: (props: HeadingProps) => <Heading level='h2' {...props} />,
  h3: (props: HeadingProps) => <Heading level='h3' {...props} />,
  h4: (props: HeadingProps) => <Heading level='h4' {...props} />,
  h5: (props: HeadingProps) => <Heading level='h5' {...props} />,
  h6: (props: HeadingProps) => <Heading level='h6' {...props} />
} as const;

// Renders smaller headings in long format
const MapLongFormatHeading: { [K in keyof typeof BlockRenderers]: keyof typeof BlockRenderers } = {
  body: 'body',
  normal: 'normal',
  disclaimer: 'disclaimer',
  blockquote: 'blockquote',
  superscript: 'superscript',
  mega: 'h2',
  epic: 'h2',
  h1: 'h2',
  h2: 'h4',
  h3: 'h5',
  h4: 'h6',
  h5: 'h6',
  h6: 'h6'
};

type HeadingOptions =
  | HeadingProps['level']
  | { as?: HeadingProps['as']; level: HeadingProps['level'] };

export type TextBlockRendererProps = {
  node: {
    style?: keyof typeof BlockRenderers;
  };
  /** DEPRECATED: use renderNormal instead */
  renderNormalAsHeading?: HeadingOptions;
  renderNormal?: React.ReactElement;
  isLongFormat?: boolean;
  className?: string;
} & React.PropsWithChildren;

export const TextBlockRenderer: React.FC<TextBlockRendererProps> = (props) => {
  const {
    node: { style: propStyle },
    isLongFormat,
    className,
    children,
    renderNormal,
    renderNormalAsHeading
  } = props;

  const { style, as } = useMemo(() => {
    if (!propStyle) {
      return { style: undefined, as: undefined };
    }

    const style = isLongFormat ? MapLongFormatHeading[propStyle] : propStyle;
    const as =
      style !== propStyle && HeadingLevels.includes(propStyle as HeadingLevel)
        ? (propStyle as HeadingTag)
        : undefined;

    return { style, as };
  }, [isLongFormat, propStyle]);

  const blockRenderersProps = useMemo(() => {
    return {
      className,
      children,
      ...(as ? { as } : {}),
      ...(style === 'normal' ? { renderNormal, renderNormalAsHeading } : {})
    };
  }, [as, className, children, renderNormal, renderNormalAsHeading]);

  return style && style in BlockRenderers
    ? BlockRenderers[style](blockRenderersProps)
    : // @ts-ignore
      BlockContent.defaultSerializers.types.block(props);
};
